import { enumStatus, afiliatedServices } from "./options";
import { operationsRoles } from "./roles";

// Admin y Supervisor pueden finalizar un caso en "Carga pendiente" aunque
// falten imagenes o pagos por cargar.
const CAN_FORCE_FINISH_PENDING_LOAD_ROLES = [
  operationsRoles.operationsAdmin,
  operationsRoles.operationsSupervisor,
];

export const hasTowService = (_case) => !!getActiveTowService(_case);

export const hasBikeService = (_case) => !!getActiveBikeService(_case);

const getActiveTowService = (_case) => {
  const [service] =
    _case?.services?.filter(
      (s) => s?.type === "Grua" && s?.status !== "Cancelado"
    ) || [];
  return service;
};

const getRoadAssistanceServices = (_case) =>
  _case?.services?.filter(
    (s) => s?.type === "Asistencia vial" && s?.status !== "Cancelado"
  ) || [];

export const isTowService = (service) => service?.type === "Grua";

// Ultimo servicio de grua no cancelado del caso. Se usa para no actualizar
// un servicio de otro tipo (ej. lastService de AV) con un gruero.
export const getLastTowService = (_case) => {
  const services =
    _case?.services?.filter(
      (s) => isTowService(s) && s?.status !== "Cancelado"
    ) || [];
  return services[services.length - 1];
};

export const getLastRoadAssistanceService = (_case) => {
  const services = getRoadAssistanceServices(_case);
  return services[services.length - 1];
};

const getActiveBikeService = (_case) => getLastRoadAssistanceService(_case);

const hasMissingTowImages = (service) =>
  !service?.towImages?.towAtOrigin ||
  !service?.towImages?.vehicleOnRoad ||
  !service?.towImages?.vehicleOnTow;

export const validateTowImages = (_case, { ignoreStage = false } = {}) => {
  const services = getActiveTowService(_case);

  if (!services || !hasMissingTowImages(services)) {
    return false;
  }

  if (ignoreStage) {
    return true;
  }

  return [enumStatus?.CUSTOMER_ON_ROAD, enumStatus?.PENDING_LOAD].includes(
    _case?.lastStage?.name
  );
};

export const validateServicePayment = (_case, { ignoreStage = false } = {}) => {
  const hasPendingPayment = _case?.services?.some(
    (s) => s?.status !== "Cancelado" && s?.pendingPayment?.pending === true
  );

  if (!hasPendingPayment) {
    return false;
  }

  if (ignoreStage) {
    return true;
  }

  return [enumStatus?.PENDING_LOAD].includes(_case?.lastStage?.name);
};

const hasMissingBikeImages = (service, _case) => {
  let hasMissingImages = false;
  if (_case?.solvedByAV) {
    if (!service?.avImageSolved) {
      hasMissingImages = true;
    }
  }
  if (!service?.avImage) {
    hasMissingImages = true;
  }

  return hasMissingImages;
};

export const validateBikeImages = (_case, { ignoreStage = false } = {}) => {
  const services = getActiveBikeService(_case);

  if (!services || !hasMissingBikeImages(services, _case)) {
    return false;
  }

  if (ignoreStage) {
    return true;
  }

  return [enumStatus?.AV_IN_ORIGIN, enumStatus?.PENDING_LOAD]?.includes(
    _case?.lastStage?.name
  );
};

// Servicio de cerrajeria del caso (puede no ser el lastService si se agregaron otros)
const locksmithServices = (_case) =>
  [_case?.lastService, ...(_case?.services || [])].filter(
    (s) => s?.product?.code === afiliatedServices.LOCKSMITH
  );

export const getLocksmithService = (_case) => {
  const services = locksmithServices(_case);
  return (
    services.find((s) => s?.hasLocksmithInvoice) ||
    services.find((s) => s?.locksmithInvoice?.file) ||
    services.find((s) => s?.locksmithInvoice) ||
    services[0]
  );
};

// El botón "Cargar Reembolso" solo se habilita cuando el listado trae la factura.
export const canLoadLocksmithReimbursement = (_case) =>
  getLocksmithService(_case)?.hasLocksmithInvoice === true;

export const hasLocksmithInvoice = (_case) => {
  const service = getLocksmithService(_case);
  return (
    service?.hasLocksmithInvoice === true || !!service?.locksmithInvoice?.file
  );
};

export const hasLocksmithReimbursement = (_case) =>
  getLocksmithService(_case)?.hasRefund === true || _case?.hasRefund === true;

// Sin factura, finalizar cancela y no descuenta el servicio.
// Con factura y sin reembolso no se puede finalizar ni descontar.
// Con factura y reembolso se finaliza directo (sin modal) y sí se descuenta.
export const locksmithFinishMode = {
  CANCEL: "cancel",
  BLOCK: "block",
  FINISH: "finish",
};

export const getLocksmithFinishMode = (_case) => {
  if (!hasLocksmithInvoice(_case)) return locksmithFinishMode.CANCEL;
  if (!hasLocksmithReimbursement(_case)) return locksmithFinishMode.BLOCK;
  return locksmithFinishMode.FINISH;
};

export const isLocksmithFinishDisabled = (_case) =>
  !!getLocksmithService(_case) &&
  hasLocksmithInvoice(_case) &&
  !hasLocksmithReimbursement(_case);

export const canFinishPendingLoadCase = (_case, role) =>
  CAN_FORCE_FINISH_PENDING_LOAD_ROLES.includes(role) ||
  (!validateTowImages(_case) &&
    !validateBikeImages(_case) &&
    !validateServicePayment(_case));
